<template>
  <component
    v-bind="$attrs"
    :is="componentType"
    ref="el"
    :rel="rel"
    :data-testid="testId"
    class="button text-cta-default relative box-border flex shrink-0 gap-2 rounded-lg leading-none text-ellipsis no-underline transition-all disabled:cursor-not-allowed [&>*]:pointer-events-none [&>*]:touch-none"
    :disabled="disabled"
    :to="to || href || ''"
    :href="componentType == 'a' ? href : undefined"
    :external="!to && href && href.startsWith('http')"
    :target="target"
    :aria-label="label"
    :class="{
      // Sizes
      'px-3': !isLinkButton && !iconOnly,
      'h-7': !isLinkButton,
      'h-5': isLinkButton && !iconOnly,

      // Icon only
      'w-7 min-w-7': !isLinkButton && iconOnly,
      'aspect-square': iconOnly,

      // Variants
      'bg-btn-bg-primary-default text-btn-fgr-primary-default group-hover/button:bg-btn-bg-primary-hover hover:bg-btn-bg-primary-hover focus:bg-btn-bg-primary-default active:bg-btn-bg-primary-pressed group-hover/button:text-btn-fgr-primary-hover hover:text-btn-fgr-primary-hover focus:text-btn-fgr-primary-default active:text-btn-fgr-primary-pressed':
        variant === ButtonVariant.Primary,
      'bg-btn-bg-primary-active text-btn-fgr-primary-active': variant === ButtonVariant.Primary && active,

      'bg-btn-bg-secondary-default text-btn-fgr-secondary-default group-hover/button:bg-btn-bg-secondary-hover hover:bg-btn-bg-secondary-hover focus:bg-btn-bg-secondary-default active:bg-btn-bg-secondary-pressed group-hover/button:fg-btn-bg-secondary-hover hover:text-btn-fgr-secondary-hover focus:text-btn-fgr-secondary-default active:text-btn-fgr-secondary-pressed':
        variant === ButtonVariant.Secondary,
      'bg-btn-bg-secondary-active! text-btn-fgr-secondary-active': variant === ButtonVariant.Secondary && active,

      'text-btn-fgr-tertiary-default group-hover/button:bg-btn-bg-tertiary-hover hover:bg-btn-bg-tertiary-hover focus:bg-btn-bg-tertiary-default active:bg-btn-bg-tertiary-pressed group-hover/button:fg-btn-bg-tertiary-hover hover:text-btn-fgr-tertiary-hover focus:text-btn-fgr-tertiary-default active:text-btn-fgr-tertiary-pressed':
        variant === ButtonVariant.Tertiary,
      'bg-btn-bg-tertiary-active text-btn-fgr-tertiary-active shadow-btn-active':
        variant === ButtonVariant.Tertiary && active,

      'bg-btn-bg-positive-default text-btn-fgr-positive-default group-hover/button:bg-btn-bg-positive-hover hover:bg-btn-bg-positive-hover focus:bg-btn-bg-positive-default active:bg-btn-bg-positive-pressed group-hover/button:fg-btn-bg-positive-hover hover:text-btn-fgr-positive-hover focus:text-btn-fgr-positive-default active:text-btn-fgr-positive-pressed':
        variant === ButtonVariant.Positive,
      'bg-btn-bg-positive-active text-btn-fgr-positive-active': variant === ButtonVariant.Positive && active,

      'bg-btn-bg-critical-default text-btn-fgr-destructive-default group-hover/button:bg-btn-bg-critical-hover hover:bg-btn-bg-critical-hover focus:bg-btn-bg-critical-default active:bg-btn-bg-critical-pressed group-hover/button:fg-btn-bg-critical-hover hover:text-btn-fgr-destructive-hover focus:text-btn-fgr-destructive-default active:text-btn-fgr-destructive-pressed':
        variant === ButtonVariant.Destructive,
      'bg-btn-bg-critical-active text-btn-fgr-destructive-active': variant === ButtonVariant.Destructive && active,

      'text-btn-fgr-ghost-default hover:text-btn-fgr-ghost-hover focus:text-btn-fgr-ghost-default active:text-btn-fgr-ghost-pressed':
        variant === ButtonVariant.Ghost,
      'text-btn-fgr-ghost-active': variant === ButtonVariant.Ghost && active,

      'text-btn-fgr-destructive-default hover:text-btn-fgr-destructive-hover focus:text-btn-fgr-destructive-default active:text-btn-fgr-destructive-pressed':
        variant === ButtonVariant.GhostDestructive,
      'text-btn-fgr-destructive-active': variant === ButtonVariant.GhostDestructive && active,

      'bg-snackbar-bgr-primary-rest hover:bg-snackbar-bgr-primary-hover focus:bg-snackbar-bgr-primary-rest active:bg-snackbar-bgr-primary-active text-snackbar-fgr-primary-rest hover:text-snackbar-fgr-primary-hover focus:text-snackbar-fgr-primary-active active:text-snackbar-fgr-primary-active':
        variant === ButtonVariant.ActionPrimary,
      'bg-snackbar-bgr-primary-active text-snackbar-fgr-primary-active':
        variant === ButtonVariant.ActionPrimary && active,

      'bg-snackbar-bgr-critical-rest hover:bg-snackbar-bgr-critical-hover focus:bg-snackbar-bgr-critical-rest active:bg-snackbar-bgr-critical-active text-snackbar-fgr-critical-rest hover:text-snackbar-fgr-critical-hover focus:text-snackbar-fgr-critical-active active:text-snackbar-fgr-critical-active':
        variant === ButtonVariant.ActionCritical,
      'bg-snackbar-bgr-critical-active text-snackbar-fgr-critical-active':
        variant === ButtonVariant.ActionCritical && active,

      // Shadows
      'shadow-btn hover:shadow-btn-hover active:shadow-btn-active': !disabled && !active && !isTextButton,
      'shadow-btn-active!': !disabled && active && !isLinkButton,

      // Disabled
      '!text-btn-fgr-disabled !shadow-none': disabled,
      '!bg-btn-bg-disabled': disabled && !isTextButton,

      // Adjustables
      'pointer-events-none touch-none': loading,
      'items-center justify-center': !textLeft,
      underline: isLink && isLinkButton,
    }"
  >
    <slot name="prefix" />
    <div
      v-if="hasContent"
      class="relative flex items-center gap-2"
      :class="{ 'text-transparent': loading, grow: grow }"
    >
      <slot />
      <div
        v-if="ticker"
        class="bg-bgr-success-primary text-body-small absolute top-0 -right-2 flex size-2 rounded-full font-medium"
      />
    </div>
    <slot name="suffix" />
    <TransitionGroup name="checkmark-show">
      <Icon v-if="checkmark" class="text-inherit" src="check" />
    </TransitionGroup>
    <CircularLoader v-if="loading" :size="16" class="absolute flex h-4 items-center justify-center" />
  </component>
</template>

<script setup lang="ts">
  const props = withDefaults(
    defineProps<{
      active?: boolean;
      checkmark?: boolean;
      disabled?: boolean;
      grow?: boolean;
      href?: string;
      iconOnly?: boolean;
      label?: string;
      loading?: boolean;
      rel?: string;
      target?: string;
      testId?: string;
      textLeft?: boolean;
      ticker?: boolean;
      to?: string | { name: string; params?: Record<string, string | number> };
      variant?: ButtonVariant;
    }>(),
    {
      active: false,
      checkmark: false,
      disabled: false,
      grow: false,
      href: undefined,
      iconOnly: false,
      label: "",
      loading: false,
      rel: undefined,
      target: "_self",
      testId: undefined,
      textLeft: false,
      ticker: false,
      to: undefined,
      variant: ButtonVariant.Secondary,
    }
  );

  const el = useTemplateRef<HTMLElement>("el");
  const slots = defineSlots();

  const isLinkButton = computed(() => [ButtonVariant.Ghost, ButtonVariant.GhostDestructive].includes(props.variant));
  const isTextButton = computed(() =>
    [
      ButtonVariant.Ghost,
      ButtonVariant.GhostDestructive,
      ButtonVariant.Tertiary,
      ButtonVariant.ActionPrimary,
      ButtonVariant.ActionCritical,
    ].includes(props.variant)
  );

  const hasContent = computed(() => {
    return slots.default?.().length > 0;
  });
  const isLink = computed(() => {
    return props.to || props.href;
  });

  // Determine component type based on props
  const componentType = computed(() => {
    if (props.href && !props.to) {
      return "a";
    } else if (props.to) {
      return resolveComponent("NuxtLink");
    }
    return "button";
  });

  defineExpose({
    el,
  });
</script>

<style>
  .spinner > *:nth-child(0) {
    -webkit-animation-delay: -0.32s;
    animation-delay: -0.32s;
  }

  .spinner > *:nth-child(1) {
    -webkit-animation-delay: -0.16s;
    animation-delay: -0.16s;
  }
</style>
